import { InferGetStaticPropsType } from "next";
import Link from "next/link";
import { useEffect } from "react";
import { useRouter } from "next/router";

import { StandaloneProductTile } from "@chef/smart-components";
import { AppShell } from "@chef/feature-app-shell";
import { useOrdersQuery, storeWrapper } from "@chef/state-management";
import { getAttribute } from "@chef/state-management/helpers";
import { Mealcards } from "@chef/smart-components";
import {
  formatDate,
  getNextCutoff,
  isAddonProduct,
  isLimitedQuantityProduct,
  isMealboxProduct,
  isStandaloneProduct,
  orderHasBeenDelivered,
} from "@chef/helpers";
import { Button, Tag } from "@chef/components";
import {
  BRAND_PATHS,
  FEATURE_FLAGS,
  ORDER_STATUS_IDS,
  ORDER_TYPE_IDS,
} from "@chef/constants";
import { OrderReceipt } from "@chef/smart-components";
import { isNotEmptyArray, sumByProperty } from "@chef/utils/array";
import { getPromotionsData, getWeekOptionsData } from "@chef/feature-sanity";
import { shouldShowDelivery } from "@chef/helpers";
import { useFlag } from "@chef/feature-flags";

import { MyMenuGetStaticProps } from "..";
import { intl } from "./MyDelivery.intl";
import { WeeksNavigation } from "../lib/WeeksNavigation";

export const MyDelivery = (
  props: InferGetStaticPropsType<typeof MyMenuGetStaticProps>,
) => {
  const router = useRouter();

  const { enabled: isWeekEditorRedesignEnabled } = useFlag(
    FEATURE_FLAGS.WEEK_EDITOR_REDESIGN,
  );

  const { order, isSuccess } = useOrdersQuery(
    {
      count: 1,
      orderTypes: [ORDER_TYPE_IDS.RECURRING],
      orderStatuses: [ORDER_STATUS_IDS.PROCESSING, ORDER_STATUS_IDS.FINISHED],
    },
    {
      selectFromResult: ({ data, isSuccess }) => ({
        order: data?.orders[0],
        isSuccess,
      }),
    },
  );

  const { week, year, promotion, weekOptions } = props;

  useEffect(() => {
    if (isSuccess) {
      let shouldRedirect = false;

      if (!order) {
        shouldRedirect = true;
      }

      if (order && !shouldShowDelivery(order.deliveryDate)) {
        shouldRedirect = true;
      }

      if (shouldRedirect) {
        router.push(BRAND_PATHS.MY_MENU_PAGE);
      }
    }
  }, [isSuccess, order, router]);

  const mealboxProduct = order?.orderLines.find((ol) =>
    isMealboxProduct(ol.variation.product),
  );

  if (!order || !mealboxProduct) {
    return;
  }

  const orderDeliveryDate = formatDate(
    new Date(order.deliveryDate),
    intl.SHORT_DATE_FORMAT,
  );

  const isDelivered = orderHasBeenDelivered(order);

  const recipes = order.orderLines
    .filter((ol) => !isStandaloneProduct(ol.variation.product))
    .flatMap((ol) => ol.recipes);

  const standaloneProducts = order.orderLines.filter(
    (ol) =>
      isStandaloneProduct(ol.variation.product) ||
      isAddonProduct(ol.variation.product) ||
      isLimitedQuantityProduct(ol.variation.product),
  );

  const numStandaloneProducts = sumByProperty(standaloneProducts, "quantity");

  const portions = mealboxProduct
    ? +(getAttribute("Portions", mealboxProduct.variation) || "0")
    : 0;
  const meals = mealboxProduct
    ? +(getAttribute("Meals", mealboxProduct.variation) || "0")
    : 0;

  const orderStatuMessage = `${
    isDelivered ? intl.ABOUT_DELIVERY_DELIVERED : intl.ABOUT_DELIVERY_ON_THE_WAY
  } ${orderDeliveryDate}`;

  const hasStandaloneProducts = isNotEmptyArray(standaloneProducts);

  return (
    <AppShell title={intl.YOUR_ORDER}>
      <AppShell.Meta />
      <AppShell.Drawer />
      <AppShell.Header
        variant={isWeekEditorRedesignEnabled ? "my-menu" : "default"}
      />
      <AppShell.AccountPausedBanner />
      <AppShell.OldVippsBanner />

      {!isWeekEditorRedesignEnabled && (
        <WeeksNavigation
          week={week}
          year={year}
          sticky
          promotion={promotion}
          weekOptions={weekOptions}
        />
      )}

      <AppShell.Main className="flex flex-col items-start justify-start min-w-full pb-12">
        <h1>
          <strong>{intl.YOUR_ORDER}</strong>
        </h1>
        <p className="mt-1 text-base md:text-lg">{orderStatuMessage}</p>
        {!!meals && (
          <>
            <div className="flex items-center gap-2 mt-3">
              <Tag>
                {meals} {intl.MEALS}
              </Tag>
              <Tag>
                {portions} {intl.PORTIONS}
              </Tag>
              {!!numStandaloneProducts && (
                <Tag>
                  {numStandaloneProducts}{" "}
                  {intl.STANDALONE_PRODUCTS.toLocaleLowerCase()}
                </Tag>
              )}
            </div>
            <h2 className="grid grid-cols-2 my-8 text-xl md:grid-cols-6">
              <strong>{intl.DINNERS}</strong>
            </h2>
            <Mealcards
              recipes={recipes}
              week={order.week}
              year={order.year}
              portions={portions}
              meals={meals}
              isDelivered={isDelivered}
              view="historic"
            />
          </>
        )}

        <div className="my-8">
          {hasStandaloneProducts && (
            <h2 className="text-xl ">
              <strong>{`${intl.STANDALONE_PRODUCTS} (${numStandaloneProducts})`}</strong>
            </h2>
          )}
        </div>

        <div className="flex flex-col flex-wrap items-start w-full md:flex-row">
          {hasStandaloneProducts && (
            <div className="flex flex-col w-full gap-2 pb-8 md:pr-8 md:w-3/5 lg:w-2/3">
              <div className="grid w-full grid-cols-1 gap-2 md:gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {standaloneProducts.map((sp) => (
                  <StandaloneProductTile
                    key={sp.variationId}
                    view="historic"
                    isPrioritisedProduct={true}
                    week={order.week}
                    year={order.year}
                    variationId={sp.variationId}
                    orderId={order.orderId}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col w-full md:w-2/5 lg:w-1/3 gap-7">
            <OrderReceipt orderId={order.orderId} className="" />

            <div className="flex flex-col w-full gap-4 md:mt-0">
              <Link
                href={BRAND_PATHS.DELIVERY_CALENDAR_HISTORY_LINK}
                passHref
                legacyBehavior
              >
                <Button outlined>{intl.VIEW_ALL_ORDERS}</Button>
              </Link>
              {isDelivered && (
                <Link
                  href={BRAND_PATHS.CLAIM_ORDER_HISTORY_HREF(order.orderId)}
                  passHref
                  legacyBehavior
                >
                  <Button outlined>
                    {intl.REPORT_PROBLEMS_OR_MISSING_ITEMS}
                  </Button>
                </Link>
              )}
              <Link
                href={BRAND_PATHS.TELL_A_FRIEND_HREF}
                passHref
                legacyBehavior
              >
                <Button outlined>{intl.REFER_A_FRIEND}</Button>
              </Link>
            </div>
          </div>
        </div>
      </AppShell.Main>
      <AppShell.Footer variant="minified" />
      <AppShell.TabBar
        variant={isWeekEditorRedesignEnabled ? "my-menu" : "default"}
      />
    </AppShell>
  );
};

export const MyDeliveryGetStaticProps = storeWrapper.getStaticProps(
  () => async (ctx) => {
    const { week, year } = getNextCutoff();

    const selectedWeek = +(ctx.params?.["week"] as string) || week;
    const selectedYear = +(ctx.params?.["year"] as string) || year;

    const weekOptions = await getWeekOptionsData(null, {
      preview: ctx.preview || false,
    });

    const promotions = await getPromotionsData();

    const currentPromotion = promotions.find(
      (p) => p.week === selectedWeek && p.year === selectedYear,
    );

    return {
      props: {
        week: selectedWeek,
        year: selectedYear,
        promotion: currentPromotion || null,
        weekOptions,
      },
      revalidate: 600,
    };
  },
);
